import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input } from '@angular/core';
import { HdFearsPanelService } from '../../fears/hd-fears-panel.service';
import { hdFearCenterForGate } from '../../fears/hd-fears.metadata';
import { HdVoicesPanelService } from '../../voices/hd-voices-panel.service';
import { hdIsThroatVoiceGate } from '../../voices/hd-voices.metadata';
import { HdMemoryPanelService } from '../../memory/hd-memory-panel.service';
import { hdIsMemoryCircuitGate } from '../../memory/hd-memory.metadata';
import { HdLovePanelService } from '../../love/hd-love-panel.service';
import { hdIsLoveGate } from '../../love/hd-love.metadata';
import { HdMysticalPanelService } from '../../mystical/hd-mystical-panel.service';
import { hdIsMysticalCircuitGate } from '../../mystical/hd-mystical.metadata';
import {
  HdBodygraphIconKey,
  getHdBodygraphIconFaName,
  getHdBodygraphIconVisualisation,
} from '../hd-bodygraph-icons';

/** Paneel achter een klikbaar poort-attribuut-icoon. */
export type HdGateAttributeIconPanel = 'fear' | 'voice' | 'memory' | 'love' | 'mystical';

/**
 * Welk paneel een icoon van deze poort opent, of `null` wanneer het enkel een
 * icoon met tooltip is: angst op de poorten van de drie bewustzijnscentra (daar
 * bestaat een angsten-paneel-sectie), voice op de elf keelpoorten, memory op de
 * twaalf geheugencircuit-poorten, love op de liefdespoorten en mystiek op de
 * acht poorten van het mystieke circuit.
 */
export function hdGateAttributeIconPanel(gate: number, key: HdBodygraphIconKey): HdGateAttributeIconPanel | null {
  if (key.startsWith('fear-')) return hdFearCenterForGate(gate) != null ? 'fear' : null;
  if (key === 'voice') return hdIsThroatVoiceGate(gate) ? 'voice' : null;
  if (key === 'memory') return hdIsMemoryCircuitGate(gate) ? 'memory' : null;
  if (key === 'love') return hdIsLoveGate(gate) ? 'love' : null;
  if (key === 'mystical') return hdIsMysticalCircuitGate(gate) ? 'mystical' : null;
  return null;
}

/**
 * Eén poort-attribuut-icoon (angst, voice, geheugen, liefde, druk, rol, …) met
 * tooltip. Iconen met een eigen paneel ({@link hdGateAttributeIconPanel}) zijn
 * een knop die dat paneel opent, gescrolld naar deze poort; de rest is een
 * icoon met tooltip.
 *
 * Gedeeld door de poort-info (kop en lijnen), de cheat-sheet-kaart en de
 * activatie-kaartjes van het Basis-paneel, zodat klikgedrag en panelen overal
 * gelijk zijn. De host zet maat en kleur via de `--hd-gate-attribute-icon-*`
 * custom properties (zie de scss) en stelt de tooltip zelf samen (bv. met het
 * angst- of voice-detail).
 */
@Component({
  selector: 'hd-gate-attribute-icon',
  templateUrl: './hd-gate-attribute-icon.component.html',
  styleUrl: './hd-gate-attribute-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdGateAttributeIconComponent {
  readonly gate = input.required<number>();
  readonly iconKey = input.required<HdBodygraphIconKey>();
  /** Tooltip en aria-label. */
  readonly label = input.required<string>();
  /** Uit = altijd een gewoon icoon, ook als er een paneel bestaat (bv. de gaststand). */
  readonly clickable = input(true, { transform: booleanAttribute });

  private readonly fearsPanel = inject(HdFearsPanelService);
  private readonly voicesPanel = inject(HdVoicesPanelService);
  private readonly memoryPanel = inject(HdMemoryPanelService);
  private readonly lovePanel = inject(HdLovePanelService);
  private readonly mysticalPanel = inject(HdMysticalPanelService);

  protected readonly faName = computed(() => getHdBodygraphIconFaName(this.iconKey()));
  protected readonly transparent = computed(() => getHdBodygraphIconVisualisation(this.iconKey()) === 'transparent');
  protected readonly panel = computed(() =>
    this.clickable() ? hdGateAttributeIconPanel(this.gate(), this.iconKey()) : null,
  );

  protected open(event: MouseEvent): void {
    const gate = this.gate();
    const origin = { x: event.clientX, y: event.clientY };
    switch (this.panel()) {
      case 'fear':
        this.fearsPanel.openForGate(gate, origin);
        return;
      case 'voice':
        this.voicesPanel.openForGate(gate, origin);
        return;
      case 'memory':
        this.memoryPanel.openForGate(gate, origin);
        return;
      case 'love':
        this.lovePanel.openForGate(gate, origin);
        return;
      case 'mystical':
        this.mysticalPanel.openForGate(gate, origin);
        return;
    }
  }
}
